import { logger } from "@core/helpers/logger";
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  setDoc,
} from "firebase/firestore";
import { BehaviorSubject } from "rxjs";
import { v4 as uuidv4 } from "uuid";

import { BaseModel, BaseModelActive } from "./base.model";
import jsonHelper from "./helpers/jsonHelper";
import firebase from "./settings/firebase-settings";

export abstract class BaseFirestoreRepository<T> {
  protected db = firebase.firestore;
  protected _collectionName: string;
  get collectionName() {
    return this._collectionName;
  }

  protected _data: BehaviorSubject<T[]> = new BehaviorSubject<T[]>([]);
  protected get data() {
    return this._data.value;
  }
  get data$() {
    return this._data.asObservable();
  }

  constructor(collectionName: string) {
    this._collectionName = collectionName;
  }

  async add(entity: BaseModel) {
    const new_uuid = uuidv4();
    const createdAt = Date.now();
    entity.id = entity.id || new_uuid;
    entity.createdAt = createdAt;
    entity.updatedAt = createdAt;
    try {
      const docRef = doc(this.db, this._collectionName, entity.id);
      await setDoc(docRef, jsonHelper.castTo<any>(entity));
      return true;
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  async update(id: string, entity: BaseModel) {
    entity.updatedAt = Date.now();
    try {
      const docRef = doc(this.db, this._collectionName, id);
      await setDoc(docRef, jsonHelper.castTo<any>(entity));
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  async delete(id: string) {
    const docRef = doc(this.db, this._collectionName, id);
    try {
      await deleteDoc(docRef);
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  async getById(id: string): Promise<T | undefined> {
    const docRef = doc(this.db, this._collectionName, id);
    const snapshot = await getDoc(docRef);
    if (!snapshot.exists()) {
      return undefined;
    }
    return jsonHelper.castTo<T>({ ...snapshot.data(), id: snapshot.id });
  }

  async getAll(order: [string, "asc" | "desc"][] = [["createdAt", "desc"]]): Promise<T[]> {
    logger().log("service", "Fetching all documents from collection:\n", this._collectionName);
    const orderByList = order.map(x => orderBy(...x));
    const q = query(collection(this.db, this._collectionName), ...orderByList);
    const snapshot = await getDocs(q);
    if (snapshot.empty) {
      return [];
    }
    const data = snapshot.docs.map(doc => jsonHelper.castTo<T>({ ...doc.data(), id: doc.id }));
    this._data.next(data);
    logger().log(
      "service",
      `Fetched ${data.length} documents from collection:\n`,
      this._collectionName,
    );
    return data;
  }

  async toggleActive(id: string) {
    const entity = (await this.getById(id)) as unknown as BaseModelActive;
    if (!entity) return;
    entity.updatedAt = Date.now();
    entity.active = !(entity.active ?? false);
    try {
      const docRef = doc(this.db, this._collectionName, id);
      await setDoc(docRef, jsonHelper.castTo<any>(entity));
    } catch (error) {
      console.error(error);
      throw error;
    }
  }
}
